import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react'
import api, { getAuthToken } from '../services/api'
import { useEncounterAudioRecorder, SpeakerLabel } from '../hooks/useEncounterAudioRecorder'

export type { SpeakerLabel } from '../hooks/useEncounterAudioRecorder'

// Types
// Keep in sync with backend: services/encounter_service.py (NoteType) and main.py (CreateEncounterRequest)
export type NoteType = 'soap' | 'hpi' | 'ap' | 'hp' | 'discharge' | 'consult' | 'procedure' | 'ipass'
export type ConsentMethod = 'verbal' | 'written' | 'electronic'
// NOTE: Frontend and backend use divergent status enums.
// Frontend-only states: 'idle', 'consent', 'paused', 'generating'
// Backend-only states: 'pending', 'consented', 'completed'
// Frontend uses 'complete' while backend uses 'completed'.
// Shared states: 'recording', 'processing', 'error'
// Frontend uses shorter names for UI state management; backend matches PostgreSQL enum values.
// See also: backend Encounter.status in main.py / encounter_service.py
export type EncounterStatus = 'idle' | 'consent' | 'recording' | 'paused' | 'processing' | 'generating' | 'complete' | 'error'

export interface TranscriptChunk {
  chunkIndex: number
  text: string
  speaker: SpeakerLabel
  startMs: number
  endMs: number
  charStart: number
  charEnd: number
  hasFabricatedTiming: boolean
}

export interface Encounter {
  publicId: string
  status: 'pending' | 'consented' | 'recording' | 'processing' | 'completed' | 'error'
  noteType: NoteType
  fullTranscript?: string
  artifactIds?: string[]
}

interface FailedChunk {
  blob: Blob
  chunkIndex: number
  speaker: SpeakerLabel
  retries: number
}

export interface EncounterContextType {
  // State
  status: EncounterStatus
  encounter: Encounter | null
  transcript: TranscriptChunk[]
  noteType: NoteType
  error: string | null
  warnings: string[]
  generatedNoteContent: string | null
  generatedArtifactId: string | null
  failedChunkCount: number
  exhaustedChunkCount: number

  // Recording state (from audio hook)
  isRecording: boolean
  isPaused: boolean
  recordingTime: number
  currentSpeaker: SpeakerLabel
  currentChunkIndex: number

  // Actions
  startEncounter: (noteType: NoteType, patientDataId?: number) => void
  recordConsent: (method: ConsentMethod) => Promise<void>
  stopRecording: () => Promise<boolean>
  markSpeaker: (speaker: SpeakerLabel) => void
  generateNote: () => Promise<void>
  resetEncounter: () => void
  togglePause: () => void
}

const EncounterContext = createContext<EncounterContextType | null>(null)

export const useEncounter = () => {
  const ctx = useContext(EncounterContext)
  if (!ctx) throw new Error('useEncounter must be used within EncounterProvider')
  return ctx
}

const MAX_CHUNK_RETRIES = 3

const getErrorMessage = (err: unknown, fallback: string): string => {
  if (err instanceof Error) return err.message
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const axiosErr = err as { response?: { data?: { detail?: string } } }
    return axiosErr.response?.data?.detail || fallback
  }
  return fallback
}

interface RawTranscriptChunk {
  chunk_index: number
  text: string
  speaker?: string
  start_ms?: number
  end_ms?: number
  char_start?: number
  char_end?: number
}

const parseTranscriptChunk = (
  data: RawTranscriptChunk,
  fallbackSpeaker: SpeakerLabel,
  fallbackChunkIndex: number,
): TranscriptChunk => {
  const hasFabricatedTiming = data.start_ms == null || data.end_ms == null
  if (data.char_start == null && data.text) {
    console.warn('TranscriptChunk missing char_start, using fallback 0')
  }
  if (hasFabricatedTiming) {
    console.warn('TranscriptChunk missing timing data (start_ms/end_ms), using fabricated values')
  }
  return {
    chunkIndex: data.chunk_index,
    text: data.text,
    speaker: (data.speaker === 'clinician' || data.speaker === 'patient' || data.speaker === 'other') ? data.speaker : fallbackSpeaker,
    startMs: data.start_ms ?? fallbackChunkIndex * 10000,
    endMs: data.end_ms ?? (fallbackChunkIndex + 1) * 10000,
    charStart: data.char_start ?? -1,
    charEnd: data.char_end ?? -1,
    hasFabricatedTiming,
  }
}

const sendAudioChunk = async (
  encPublicId: string,
  blob: Blob,
  chunkIndex: number,
  speaker: SpeakerLabel,
  signal?: AbortSignal,
) => {
  const formData = new FormData()
  formData.append('file', blob, `chunk_${chunkIndex}.wav`)
  formData.append('chunk_index', String(chunkIndex))
  formData.append('speaker', speaker)
  formData.append('start_ms', String(chunkIndex * 10000))
  formData.append('end_ms', String((chunkIndex + 1) * 10000))

  return api.post(
    `/api/v1/encounters/${encPublicId}/audio-chunk`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' }, signal }
  )
}

export const EncounterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [status, setStatus] = useState<EncounterStatus>('idle')
  const [encounter, setEncounter] = useState<Encounter | null>(null)
  const [transcript, setTranscript] = useState<TranscriptChunk[]>([])
  const [noteType, setNoteType] = useState<NoteType>('soap')
  const [error, setError] = useState<string | null>(null)
  const [generatedNoteContent, setGeneratedNoteContent] = useState<string | null>(null)
  const [generatedArtifactId, setGeneratedArtifactId] = useState<string | null>(null)
  const [warnings, setWarnings] = useState<string[]>([])
  const [failedChunks, setFailedChunks] = useState<FailedChunk[]>([])
  const [exhaustedChunkCount, setExhaustedChunkCount] = useState(0)
  const [retryTrigger, setRetryTrigger] = useState(0)

  const [patientDataId, setPatientDataId] = useState<number | undefined>()
  const encounterRef = useRef<Encounter | null>(null)
  const statusRef = useRef(status)
  useEffect(() => { statusRef.current = status }, [status])
  const exhaustedChunkCountRef = useRef(exhaustedChunkCount)
  useEffect(() => { exhaustedChunkCountRef.current = exhaustedChunkCount }, [exhaustedChunkCount])

  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const failedChunksRef = useRef<FailedChunk[]>([])
  useEffect(() => { failedChunksRef.current = failedChunks }, [failedChunks])

  const chunkAbortRef = useRef<AbortController | null>(null)
  const generateAbortRef = useRef<AbortController | null>(null)

  // Retry failed chunks periodically (setTimeout chain to prevent overlap)
  useEffect(() => {
    if (failedChunks.length === 0 || !encounterRef.current) {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current)
        retryTimerRef.current = null
      }
      return
    }

    const controller = new AbortController()

    const retryNext = async () => {
      if (controller.signal.aborted) return

      const current = failedChunksRef.current
      const toRetry = current.filter(c => c.retries < MAX_CHUNK_RETRIES)
      const exhausted = current.filter(c => c.retries >= MAX_CHUNK_RETRIES)

      if (exhausted.length > 0) {
        setExhaustedChunkCount(prev => prev + exhausted.length)
        setFailedChunks(prev => prev.filter(c => c.retries < MAX_CHUNK_RETRIES))
        setWarnings(prev => [
          ...prev,
          `${exhausted.length} audio segment(s) could not be uploaded and may be missing from the transcript.`,
        ])
      }
      if (toRetry.length === 0) return

      const chunk = toRetry[0]
      const enc = encounterRef.current
      if (!enc) return

      try {
        const response = await sendAudioChunk(enc.publicId, chunk.blob, chunk.chunkIndex, chunk.speaker, controller.signal)

        if (controller.signal.aborted) return

        if (response.data?.text != null) {
          setTranscript(prev => [...prev, parseTranscriptChunk(response.data, chunk.speaker, chunk.chunkIndex)])
          setFailedChunks(prev => prev.filter(c => c.chunkIndex !== chunk.chunkIndex))
        } else {
          console.warn('Empty transcript data for chunk', chunk.chunkIndex)
          setFailedChunks(prev => prev.map(c =>
            c.chunkIndex === chunk.chunkIndex ? { ...c, retries: c.retries + 1 } : c
          ))
        }
      } catch (e) {
        if (controller.signal.aborted) return
        console.error('Chunk retry failed for index', chunk.chunkIndex, e)
        setFailedChunks(prev => prev.map(c =>
          c.chunkIndex === chunk.chunkIndex ? { ...c, retries: c.retries + 1 } : c
        ))
      }

      if (!controller.signal.aborted) {
        retryTimerRef.current = setTimeout(retryNext, 5000)
      }
    }

    retryTimerRef.current = setTimeout(retryNext, 5000)

    return () => {
      controller.abort()
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current)
        retryTimerRef.current = null
      }
    }
  }, [retryTrigger, failedChunks.length])

  const handleAudioChunk = useCallback(async (blob: Blob, chunkIndex: number, speaker: SpeakerLabel) => {
    if (!encounterRef.current) {
      console.warn('handleAudioChunk called with no active encounter, chunk', chunkIndex, 'dropped')
      setWarnings(prev => [...prev, `Audio chunk ${chunkIndex} was dropped — no active encounter.`])
      return
    }

    try {
      const response = await sendAudioChunk(encounterRef.current.publicId, blob, chunkIndex, speaker, chunkAbortRef.current?.signal)

      // Fix #247: Encounter may have been reset while chunk was in flight
      if (!encounterRef.current) return

      if (response.data?.text != null) {
        setTranscript(prev => [...prev, parseTranscriptChunk(response.data, speaker, chunkIndex)])
      } else {
        console.warn('Empty transcript data for chunk', chunkIndex)
        setFailedChunks(prev => [...prev, { blob, chunkIndex, speaker, retries: 0 }])
        setRetryTrigger(t => t + 1)
        return
      }
    } catch (err) {
      // Fix #247: Don't enqueue failed chunks after encounter was reset/aborted
      if (err instanceof DOMException && err.name === 'AbortError') return
      if (!encounterRef.current) return
      console.error('Failed to process audio chunk:', err)
      setFailedChunks(prev => [...prev, { blob, chunkIndex, speaker, retries: 0 }])
      setRetryTrigger(t => t + 1)
    }
  }, [])

  const audioRecorder = useEncounterAudioRecorder({
    onChunk: handleAudioChunk,
    onError: (event: Event) => {
      const detail = (event as ErrorEvent)?.message
      setError(detail || 'Recording failed unexpectedly. Please try again.')
      setStatus('error')
    },
    chunkIntervalMs: 10000,
  })

  const startEncounter = useCallback((type: NoteType, dataId?: number) => {
    setNoteType(type)
    setPatientDataId(dataId)
    setStatus('consent')
    setError(null)
    setWarnings([])
    setTranscript([])
    setFailedChunks([])
    setExhaustedChunkCount(0)
    setGeneratedNoteContent(null)
    setGeneratedArtifactId(null)
  }, [])

  const recordConsent = useCallback(async (method: ConsentMethod) => {
    try {
      const currentNoteType = noteType
      const createRes = await api.post('/api/v1/encounters', { note_type: currentNoteType, patient_data_id: patientDataId })
      const enc: Encounter = {
        publicId: createRes.data.public_id,
        status: createRes.data.status,
        noteType: currentNoteType,
      }
      setEncounter(enc)
      encounterRef.current = enc
      chunkAbortRef.current = new AbortController()

      await api.post(`/api/v1/encounters/${enc.publicId}/consent`, { consent_method: method })

      setStatus('recording')

      await api.post(`/api/v1/encounters/${enc.publicId}/start-recording`)

      // Start browser recording; rollback backend state if mic fails
      try {
        await audioRecorder.startRecording()
      } catch (micErr) {
        // Mic permission denied or hardware error — revert backend state
        try {
          await api.post(`/api/v1/encounters/${enc.publicId}/mark-error`)
        } catch (cleanupErr) {
          console.warn('Failed to mark encounter as error during mic cleanup:', cleanupErr)
        }
        // Clear ref so outer catch skips duplicate mark-error
        encounterRef.current = null
        setEncounter(null)
        throw micErr
      }
    } catch (err: unknown) {
      // Best-effort backend cleanup for orphaned encounter
      if (encounterRef.current) {
        try {
          await api.post(`/api/v1/encounters/${encounterRef.current.publicId}/mark-error`)
        } catch (markErr) { console.warn('Best-effort mark-error failed during cleanup:', markErr) }
      }
      setEncounter(null)
      encounterRef.current = null
      setError(getErrorMessage(err, 'Failed to start encounter'))
      setStatus('error')
    }
  }, [noteType, patientDataId, audioRecorder])

  const stopRecordingAction = useCallback(async (): Promise<boolean> => {
    try {
      setStatus('processing')
      await audioRecorder.stopRecording()

      if (!encounterRef.current) {
        console.error('stopRecording: no active encounter — cannot notify backend')
        setStatus('error')
        setError('Recording stopped but encounter was not found')
        return false
      }

      const res = await api.post(
        `/api/v1/encounters/${encounterRef.current.publicId}/stop-recording`,
        { exhausted_chunk_count: exhaustedChunkCountRef.current }
      )
      const backendStatus = res.data?.status ?? 'processing'
      setEncounter(prev => prev ? { ...prev, status: backendStatus } : null)
      return true
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to stop recording'))
      setStatus('error')
      return false
    }
  }, [audioRecorder])

  const generateNoteAction = useCallback(async () => {
    if (!encounterRef.current) {
      setError('No active encounter. Please start a new recording.')
      setStatus('error')
      return
    }

    // Abort any previous generation stream before starting a new one
    generateAbortRef.current?.abort()
    const controller = new AbortController()
    generateAbortRef.current = controller

    try {
      setStatus('generating')

      // Use fetch API for proper SSE streaming (axios doesn't support browser streaming)
      const token = await getAuthToken()
      const baseUrl = api.defaults.baseURL || ''
      const url = `${baseUrl}/api/v1/encounters/${encounterRef.current.publicId}/generate-note`

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Accept': 'text/event-stream',
          ...(token ? { 'Authorization': token } : {}),
        },
        signal: controller.signal,
      })

      if (!response.ok) {
        throw new Error(`Generate note failed: ${response.status}`)
      }

      const reader = response.body?.getReader()
      if (!reader) throw new Error('No response body')

      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || '' // Keep incomplete line in buffer

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const jsonStr = line.slice(6).trim()
          if (!jsonStr || jsonStr === '[DONE]') continue

          try {
            const event = JSON.parse(jsonStr)
            if (event.type === 'complete') {
              setGeneratedArtifactId(event.artifact_id || null)
              setGeneratedNoteContent(event.content || '')
              setStatus('complete')
              return
            }
            if (event.type === 'error') {
              setError(event.message || 'Note generation failed')
              setStatus('error')
              return
            }
            if (event.type === 'status') {
              continue
            }
            if (event.type === 'warning') {
              setWarnings(prev => [...prev, event.message])
              continue
            }
          } catch (e) {
            if (e instanceof SyntaxError) {
              console.warn('Malformed SSE event:', jsonStr)
            } else {
              throw e
            }
          }
        }
      }

      // If we got here without a complete event, mark as error
      if (statusRef.current !== 'complete') {
        setError('Note generation completed without result')
        setStatus('error')
      }
    } catch (err: unknown) {
      if (controller.signal.aborted) return // User cancelled — don't update state
      setError(getErrorMessage(err, 'Failed to generate note'))
      setStatus('error')
    }
  }, [])

  const resetEncounter = useCallback(() => {
    const enc = encounterRef.current
    chunkAbortRef.current?.abort()
    chunkAbortRef.current = null
    generateAbortRef.current?.abort()
    generateAbortRef.current = null
    audioRecorder.discardRecording()
    setStatus('idle')
    setEncounter(null)
    encounterRef.current = null
    setTranscript([])
    setNoteType('soap')
    setPatientDataId(undefined)
    setError(null)
    setWarnings([])
    setFailedChunks([])
    setExhaustedChunkCount(0)
    setGeneratedNoteContent(null)
    setGeneratedArtifactId(null)

    // Best-effort backend cleanup with single retry — don't block UI reset
    // Skip mark-error for encounters already in a terminal state
    const terminalStatuses = ['complete', 'completed', 'error']
    if (enc?.publicId && !terminalStatuses.includes(enc.status ?? '')) {
      api.post(`/api/v1/encounters/${enc.publicId}/mark-error`).catch((err) => {
        console.warn('Failed to mark encounter as error, retrying:', err)
        // Retry once after a short delay before giving up
        setTimeout(() => {
          api.post(`/api/v1/encounters/${enc.publicId}/mark-error`).catch((retryErr) => {
            console.warn('Failed to mark discarded encounter as error after retry:', retryErr)
          })
        }, 1000)
      })
    }
  }, [audioRecorder])

  const togglePause = useCallback(() => {
    if (status !== 'recording' && status !== 'paused') return
    audioRecorder.togglePause()
    setStatus(prev => prev === 'recording' ? 'paused' : 'recording')
  }, [audioRecorder, status])

  const value: EncounterContextType = {
    status,
    encounter,
    transcript,
    noteType,
    error,
    warnings,
    generatedNoteContent,
    generatedArtifactId,
    failedChunkCount: failedChunks.length,
    exhaustedChunkCount,
    isRecording: audioRecorder.isRecording,
    isPaused: audioRecorder.isPaused,
    recordingTime: audioRecorder.recordingTime,
    currentSpeaker: audioRecorder.currentSpeaker,
    currentChunkIndex: audioRecorder.currentChunkIndex,
    startEncounter,
    recordConsent,
    stopRecording: stopRecordingAction,
    markSpeaker: audioRecorder.markSpeaker,
    generateNote: generateNoteAction,
    resetEncounter,
    togglePause,
  }

  return (
    <EncounterContext.Provider value={value}>
      {children}
    </EncounterContext.Provider>
  )
}
